import { routesPath } from 'src/router/routes';
import { selectDetachedRoute } from 'src/utils/routeHelpers';
import { replacePathParams } from 'src/utils/replacePathWithParams';
import { getURLParams } from '@dynamika-shared/utils/saga';
import { store } from '@restore/store';

/**
 * Opens the detached reader-task viewer in a new window. Called by the main
 * tab when the user clicks "detach"; replaces the legacy
 * `WebSocketReader.saga.startConnectionWorker(mainTab=true)` flow.
 *
 * Returns the new window reference (or null if `window.open` was blocked by
 * the browser).
 */
export const openDetachedWindow = (): Window | null => {
  const userRole = (
    store.getState() as { auth?: { data?: { selectedRole?: string } } }
  )?.auth?.data?.selectedRole;

  const pathSegs = window.location.pathname.split('/').filter(Boolean);
  const subTaskRouteSegs = routesPath.readerSubTaskViewerDetached
    .split('/')
    .filter(Boolean);
  const isSubTask = pathSegs.length === subTaskRouteSegs.length;

  const route = selectDetachedRoute(isSubTask, userRole);
  const params = getURLParams(route) as Record<string, string>;
  const { id, taskId, subTaskId } = params;

  const url = replacePathParams(
    route,
    isSubTask ? { id, taskId, subTaskId } : { id, taskId }
  );
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set('mainWindowName', window.name);
  const urlWithSearchParams = `${url}?${searchParams.toString()}`;

  /**
   * Plain terms
   * -----------
   * Always open a new window without navigating the existing reader. The SID
   * temporarily travels in the URL because browsers may clear `window.name`
   * during the navigation to the deployed application.
   *
   * Technical
   * ---------
   * SID already names the main window and viewer iframe. Using it as the
   * `window.open` target would reuse the main window instead of creating a
   * popup. The detached host restores `window.name` from `mainWindowName`,
   * removes the parameter from the URL, and only then creates its RPC peer.
   */
  return window.open(urlWithSearchParams, '_blank');
};
